import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Confirm from '../../../components/Confirm';
import { createLineBindCode, getLineStatus, unlinkLine } from '../api/settingsApi';

// Gocochain Support LINE official account "add friend" link (basic ID @939czyiq).
// Override per environment with VITE_LINE_ADD_FRIEND_URL if the OA changes.
const LINE_OA_ADD_FRIEND_URL = import.meta.env.VITE_LINE_ADD_FRIEND_URL || 'https://line.me/R/ti/p/%40010cwdan';

const POLL_INTERVAL_MS = 4000;
const DEFAULT_TTL_SECONDS = 600;

// "Connect LINE" section for the Settings page. A user can link *multiple* LINE
// accounts: we render the list of linked accounts (each removable) and drive the
// one-time-code handshake to add another — poll /line/status until a new account appears.
function LineConnectSection() {
    const { t } = useTranslation(undefined, { keyPrefix: 'Information.line' });
    const errorText = t('error') || 'Something went wrong. Please try again.';

    const [view, setView] = useState('loading'); // 'loading' | 'ready'
    const [accounts, setAccounts] = useState<any[]>([]); // [{ line_user_id, display_name, picture_url }]
    const [connecting, setConnecting] = useState(false); // add-account code panel visible
    const [code, setCode] = useState('');
    const [expired, setExpired] = useState(false);
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');
    const [unlinkTarget, setUnlinkTarget] = useState<any>(null); // account pending unlink confirmation

    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mountedRef = useRef(true);
    // line_user_ids present when the current code was minted — a new id means "bound".
    const baselineIdsRef = useRef(new Set());

    const stopTimers = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    };

    // Initial status check + cleanup of any running timers on unmount.
    useEffect(() => {
        mountedRef.current = true;
        (async () => {
            try {
                const data = await getLineStatus();
                if (!mountedRef.current) return;
                setAccounts(Array.isArray(data?.accounts) ? data.accounts : []);
                setView('ready');
            } catch (err) {
                console.error('Failed to load LINE status', err);
                if (mountedRef.current) setView('ready');
            }
        })();
        return () => {
            mountedRef.current = false;
            stopTimers();
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
        };
    }, []);

    // Keep asking /status; a linked account with an id we hadn't seen means the bind landed.
    const pollStatus = async () => {
        try {
            const data = await getLineStatus();
            if (!mountedRef.current) return;
            const next = Array.isArray(data?.accounts) ? data.accounts : [];
            const gotNew = next.some((acc: any) => !baselineIdsRef.current.has(acc.line_user_id));
            if (gotNew) {
                stopTimers();
                setAccounts(next);
                setCode('');
                setExpired(false);
                setError('');
                setConnecting(false);
            }
        } catch (err) {
            console.error('Failed to poll LINE status', err);
        }
    };

    const startCountdownAndPoll = (expiresIn: any) => {
        stopTimers();
        setExpired(false);
        const ttl = expiresIn > 0 ? expiresIn : DEFAULT_TTL_SECONDS;
        const expiresAt = Date.now() + ttl * 1000;

        tickRef.current = setInterval(() => {
            if (Date.now() >= expiresAt) {
                setExpired(true);
                stopTimers(); // also stops polling — cap polling to the code's TTL window
            }
        }, 1000);

        pollRef.current = setInterval(pollStatus, POLL_INTERVAL_MS);
    };

    // Each call mints a fresh single-use code, so request once per Connect / regenerate.
    const requestCode = async () => {
        setBusy(true);
        setError('');
        setCopied(false);
        setConnecting(true);
        // Snapshot the currently-linked ids so polling can tell the new account apart.
        baselineIdsRef.current = new Set(accounts.map((acc: any) => acc.line_user_id));
        try {
            const data = await createLineBindCode();
            if (!mountedRef.current) return;
            setCode(data?.code ?? '');
            startCountdownAndPoll(data?.expires_in ?? DEFAULT_TTL_SECONDS);
        } catch (err) {
            console.error('Failed to create LINE bind code', err);
            if (mountedRef.current) setError(errorText);
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    };

    // Back out of the add-account flow without minting/keeping a code.
    const cancelConnect = () => {
        stopTimers();
        setConnecting(false);
        setCode('');
        setExpired(false);
        setError('');
        setCopied(false);
    };

    const handleCopy = async () => {
        if (!code) return;
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
            copyTimerRef.current = setTimeout(() => {
                if (mountedRef.current) setCopied(false);
            }, 1500);
        } catch (err) {
            console.error('Clipboard write failed', err);
        }
    };

    const handleUnlinkConfirmed = async () => {
        const target = unlinkTarget;
        setUnlinkTarget(null);
        if (!target) return;
        setBusy(true);
        try {
            await unlinkLine(target.line_user_id);
            if (!mountedRef.current) return;
            setAccounts((prev: any) => prev.filter((acc: any) => acc.line_user_id !== target.line_user_id));
        } catch (err) {
            console.error('Failed to unlink LINE', err);
            if (mountedRef.current) setError(errorText);
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    };

    const greenButton =
        'flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#06C755] text-base font-semibold text-white shadow-md transition hover:bg-[#05A648] disabled:cursor-not-allowed disabled:opacity-60 md:h-11 md:w-[400px] md:rounded-lg md:shadow-none';
    const outlineButton =
        'flex h-12 w-full items-center justify-center gap-2 rounded-full border border-slate-300 bg-transparent text-base font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 md:h-11 md:w-[400px] md:rounded-lg';

    const renderAccount = (account: any) => {
        const name = account.display_name || t('lineUser') || 'LINE user';
        return (
            <div key={account.line_user_id} className='flex items-center gap-4 md:w-[400px]'>
                {account.picture_url ? (
                    <img
                        src={account.picture_url}
                        alt={name}
                        className='h-14 w-14 shrink-0 rounded-full object-cover'
                    />
                ) : (
                    <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#06C755] text-xl font-semibold text-white'>
                        {name.charAt(0).toUpperCase()}
                    </div>
                )}
                <div className='min-w-0 flex-1'>
                    <p className='truncate text-base font-semibold text-slate-900 font-["Roboto"]'>{name}</p>
                    <p className='mt-0.5 text-sm text-[#06C755] font-["Roboto"]'>{t('connected') || 'Connected'}</p>
                </div>
                <button
                    type='button'
                    onClick={() => setUnlinkTarget(account)}
                    disabled={busy}
                    className='shrink-0 rounded-full border border-slate-300 px-4 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60'
                >
                    {t('unlink') || 'Unlink'}
                </button>
            </div>
        );
    };

    // The one-time-code handshake panel shown while adding an account.
    const renderCodePanel = () => (
        <div className='space-y-4'>
            <p className='text-sm font-medium text-slate-600 font-["Roboto"]'>{t('instructions')}</p>
            {/* Must stay a real anchor. window.open() is a script-initiated navigation, so iOS
                never matches the line.me universal link and the Gocochain app's WebView just
                loads line.me in place — where LINE's "Open LINE" interstitial fires the line://
                scheme that the WebView cannot handle, leaving the tap dead. A user-activated
                link lets the OS hand the URL straight to the LINE app instead. */}
            <a
                href={LINE_OA_ADD_FRIEND_URL}
                target='_blank'
                rel='noopener noreferrer'
                className='flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#06C755]/10 text-[16px] font-semibold text-[#06C755] transition hover:bg-[#06C755]/20 md:h-11 md:w-[400px]'
            >
                {t('addOa') || 'Add LINE OA'}
            </a>

            <div className='md:w-[400px]'>
                <div
                    className={`relative flex items-center justify-start rounded-2xl border px-5 py-4 ${
                        expired ? 'border-slate-200 bg-slate-100 opacity-60' : 'border-slate-200 bg-slate-50'
                    }`}
                >
                    <span className='font-mono text-2xl font-bold tracking-[0.25em] text-[#1E2B7C] md:text-3xl'>
                        {code}
                    </span>
                    <button
                        type='button'
                        onClick={handleCopy}
                        disabled={expired}
                        className='absolute right-4 top-1/2 -translate-y-1/2 shrink-0 rounded-full border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50'
                    >
                        {copied ? t('copied') || 'Copied' : t('copy') || 'Copy'}
                    </button>
                    {expired && (
                        <span className='absolute bottom-1.5 left-4 text-[10px] font-medium leading-none text-red-500 font-["Roboto"]'>
                            {t('expired') || 'Code expired'}
                        </span>
                    )}
                </div>
            </div>

            {error && <p className='text-sm text-red-500 font-["Roboto"]'>{error}</p>}

            {expired && (
                <button type='button' onClick={requestCode} disabled={busy} className={greenButton}>
                    {t('generateNew') || 'Generate new code'}
                </button>
            )}

            <button
                type='button'
                onClick={cancelConnect}
                className='inline-flex items-center justify-center rounded-full bg-red-500/10 px-4 py-1.5 text-sm font-medium text-red-500 transition hover:bg-red-500/20'
            >
                {t('cancel') || 'Cancel'}
            </button>
        </div>
    );

    const renderBody = () => {
        if (view === 'loading') {
            return <p className='text-sm text-slate-500 font-["Roboto"]'>{t('loading') || 'Loading…'}</p>;
        }

        const hasAccounts = accounts.length > 0;

        return (
            <div className='space-y-5'>
                {hasAccounts && (
                    <div className='space-y-3'>
                        <p className='text-sm font-semibold text-slate-500 font-["Roboto"]'>
                            {t('connectedAccounts') || 'Connected accounts'}
                        </p>
                        {accounts.map(renderAccount)}
                    </div>
                )}

                {connecting ? (
                    renderCodePanel()
                ) : (
                    <div className='space-y-3'>
                        {!hasAccounts && (
                            <p className='text-sm font-medium text-slate-600 font-["Roboto"]'>{t('intro')}</p>
                        )}
                        {error && <p className='text-sm text-red-500 font-["Roboto"]'>{error}</p>}
                        <button
                            type='button'
                            onClick={requestCode}
                            disabled={busy}
                            className={hasAccounts ? outlineButton : greenButton}
                        >
                            {hasAccounts
                                ? t('connectAnother') || 'Connect another account'
                                : t('connect') || 'Connect LINE'}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <section className='mt-8 space-y-5 md:mt-8 md:space-y-6 md:border-b md:border-slate-300 md:pb-8'>
            <div>
                <p className='text-[#1E2B7C] text-xl font-semibold font-["Roboto"]'>
                    {t('sectionTitle') || 'LINE Notification'}
                </p>
                <div className='mt-3 h-px w-full bg-slate-200' />
            </div>
            {renderBody()}
            <Confirm
                openConfirm={unlinkTarget !== null}
                setOpenConfirm={(open: any) => {
                    if (!open) setUnlinkTarget(null);
                }}
                handleConfirm={handleUnlinkConfirmed}
                records={[]}
                tKey='Information.line.Confirm'
            />
        </section>
    );
}

export default LineConnectSection;
